import geolocation from 'geolocation';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import Identification from '../components/Identification';
import Instructions from '../components/Instructions/index';
import Loading from '../components/Loading';
import Result from '../components/Result';
import UploadDocument from '../components/UploadDocument';
import api from '../services/api';

interface StepContextData {
  documentNum: string;
  name: string;
  imageDoc: string;
  currentStep: number;
  currentValidation: string;
  result: string;
  documentType: number;
  handleSetDocument: (cpfParam: string) => void;
  handleSetName: (nameParam: string) => void;
  handleSetImage: (imageParam: string) => void;
  changeStep: () => React.ReactElement;
  handleChangeCurrentStep: () => void;
  handleReturnCurrentStep: () => void;
  handleChangeCurrentValidation: (validation: string) => void;
  clearData: () => void;
  setResult: React.Dispatch<React.SetStateAction<string>>;
  setDocumentType: React.Dispatch<React.SetStateAction<number>>;
  ImageToBase64: (file: File) => void;
  getUserLocation: () => void;
  messageError: string;
  setMessageError: (value: string) => void;
  setServiceError: (value: string) => void;
  serviceError: string;
}

const StepContext = createContext<StepContextData>({} as StepContextData);

const StepProvider: React.FC = ({ children }) => {
  const [documentNum, setDocumentNum] = useState('');
  const [name, setName] = useState('');
  const [imageDoc, setImageDoc] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [currentValidation, setCurrentValidation] = useState('');
  const [result, setResult] = useState('');
  const [messageError, setMessageError] = useState('');
  const [documentType, setDocumentType] = useState(1);
  const [loading, setLoading] = useState(false);
  const [serviceError, setServiceError] = useState('');

  const history = useHistory();

  const handleChangeCurrentValidation = useCallback((validation: string) => {
    setCurrentValidation(validation);
  }, []);

  const handleChangeCurrentStep = useCallback(() => {
    setCurrentStep(currentStep + 1);
  }, [currentStep]);

  const handleReturnCurrentStep = useCallback(() => {
    setCurrentStep(currentStep - 1);
  }, [currentStep]);

  const changeStep = useCallback(() => {
    switch (currentStep) {
      case 1:
        return <Identification />;
      case 2:
        return <Instructions />;
      case 3:
        return <UploadDocument />;
      case 4:
        return <Result />;
      default:
        return <></>;
    }
  }, [currentStep]);

  const handleSetDocument = useCallback((cpfParam: string) => {
    setDocumentNum(cpfParam);
  }, []);

  const handleSetName = useCallback((nameParam: string) => {
    setName(nameParam);
  }, []);

  const ImageToBase64 = (file: File): void => {
    const reader: FileReader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (): void => {
      handleSetImage(reader.result as string);
    };
    reader.onerror = () => {
      return false;
    };
  };

  const handleSetImage = useCallback((imageParam: string) => {
    setImageDoc(imageParam);
  }, []);

  const clearData = useCallback(() => {
    handleSetDocument('');
    handleSetImage('');
    handleSetName('');
    setCurrentStep(1);
    handleChangeCurrentValidation('');
    setMessageError('');
    setResult('');
    setServiceError('');
  }, []);

  const getUserLocation = () => {
    setLoading(true);
    geolocation.getCurrentPosition((err, position) => {
      if (err) {
        setLoading(false);
        history.push('/geolocationDenied');
        return;
      }

      const { latitude, longitude } = position.coords;

      if (!latitude || !longitude) {
        history.push('/geolocationDenied');
        return;
      }

      localStorage.setItem('@SWINTbalcao:latitude', String(latitude));
      localStorage.setItem('@SWINTbalcao:longitude', String(longitude));
      api.defaults.headers.lat = latitude;
      api.defaults.headers.lng = longitude;
      setLoading(false);
    });
  };

  useEffect(() => {
    getUserLocation();
  }, []);

  return (
    <StepContext.Provider
      value={{
        documentNum,
        name,
        imageDoc,
        currentStep,
        result,
        documentType,
        handleSetDocument,
        handleSetName,
        handleSetImage,
        ImageToBase64,
        changeStep,
        handleChangeCurrentStep,
        handleReturnCurrentStep,
        currentValidation,
        handleChangeCurrentValidation,
        clearData,
        setResult,
        setDocumentType,
        getUserLocation,
        setMessageError,
        messageError,
        setServiceError,
        serviceError,
      }}
    >
      {children}
      {loading && <Loading />}
    </StepContext.Provider>
  );
};

function useSteps(): StepContextData {
  const context = useContext(StepContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { StepProvider, useSteps };
